import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCms } from '../context/CmsContext';
import { SingleSlider } from '../components/BeforeAfterSlider';

export default function CaseDetail() {
  const { siteSettings } = useCms();
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  const getCdnUrl = (url, width = 800) => {
    if (!url) return '';
    if (url.includes('assets.auxodev.dk/assets/')) {
      return url.replace('assets.auxodev.dk/assets/', `assets.auxodev.dk/cdn-cgi/image/width=${width},quality=80,format=auto/assets/`);
    }
    return url;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);

    // Fetch single case by ID or SLUG from CMS
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
    const fetchUrl = isUuid
      ? `https://api.dev.auxo.dk/public/content/cases/${id}`
      : `https://api.dev.auxo.dk/public/by-slug/da/${id}`;

    fetch(fetchUrl)
      .then((res) => {
        if (!res.ok) throw new Error('Case not found');
        return res.json();
      })
      .then((data) => {
        setCaseData(data);
      })
      .catch((err) => {
        console.error('Error fetching case detail:', err);
        navigate('/404', { replace: true });
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <i className="fa-solid fa-spinner animate-spin text-brand-green text-4xl" />
          <p className="text-zinc-500 font-mono text-xs">Henter casen...</p>
        </div>
      </div>
    );
  }

  if (!caseData) return null;

  return (
    <>
      <Helmet>
        <title>{`${caseData.title?.da} - Ecolaser ApS`}</title>
        <meta name="description" content={(caseData.description?.da || '').replace(/<[^>]*>/g, '').slice(0, 155)} />
        <meta property="og:title" content={`${caseData.title?.da} - Ecolaser ApS`} />
        <meta property="og:description" content={(caseData.description?.da || '').replace(/<[^>]*>/g, '').slice(0, 155)} />
        <meta property="og:image" content={caseData.image?.[0]} />
        <html lang="da" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-44 pb-28 md:pt-52 md:pb-36 overflow-hidden bg-zinc-950">
        <div className="absolute inset-0 z-0">
          <img
            src={getCdnUrl(caseData.image?.[0], 1800)}
            alt={caseData.title?.da}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-zinc-950/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-left flex flex-col items-start gap-3">
          <h1 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight max-w-4xl">
            {caseData.title?.da}
          </h1>
        </div>
      </section>

      {/* Case Content Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl space-y-12">
            {/* Verbatim Text */}
            <div 
              className="prose prose-zinc max-w-none text-zinc-800 leading-relaxed text-base md:text-lg space-y-6 [&_h3]:text-base [&_h3]:md:text-lg [&_h3]:font-bold [&_h3]:text-zinc-900 [&_h3]:mt-6 [&_h3]:mb-2 [&_h2]:text-lg [&_h2]:md:text-xl [&_h2]:font-bold [&_h2]:text-zinc-900 [&_h2]:mt-8 [&_h2]:mb-3 [&_p]:text-zinc-800 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-3 [&_ul]:my-6 [&_ul]:text-zinc-800 [&_li]:leading-relaxed [&_strong]:font-bold [&_strong]:text-zinc-950"
              dangerouslySetInnerHTML={{ __html: caseData.description?.da || '' }}
            />

            {/* Interaktiv Før/Efter Kvist for Sorø Station */}
            {(caseData.id === '6727948c-aac3-48a5-ab7a-78835af53318' || caseData.slug?.da?.includes('soroe') || caseData.title?.da?.includes('Sorø')) && (
              <div className="pt-10 border-t border-zinc-100">
                <div className="mb-6">
                  <span className="text-xs font-semibold text-brand-green uppercase tracking-wider block mb-1">
                    Interaktiv Før / Efter
                  </span>
                  <h3 className="text-xl md:text-2xl font-bold text-zinc-900 flex items-center gap-2">
                    <i className="fa-solid fa-sliders text-brand-green" /> Komplet laserafrensning af Sorø station fra 1856
                  </h3>
                  <p className="text-zinc-600 text-sm md:text-base mt-2">
                    Bevaringsværdig kulturarv kræver den mest skånsomme behandling.
                  </p>
                </div>
                <div className="max-w-xl mx-auto">
                  <SingleSlider
                    caseData={{
                      title: 'Komplet laserafrensning af Sorø station fra 1856',
                      description: 'Bevaringsværdig kulturarv kræver den mest skånsomme behandling.',
                      category: 'Sorø Station Kvist',
                      before: 'https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/soroe-kvist-foer.jpg',
                      after: 'https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/soroekvist-efter.jpg'
                    }}
                  />
                </div>
              </div>
            )}

            {/* Video Section */}
            {caseData.video && Array.isArray(caseData.video) && caseData.video.length > 0 && (
              <div className="pt-10 border-t border-zinc-100">
                <h3 className="text-xl font-bold text-zinc-900 mb-6 flex items-center gap-2">
                  <i className="fa-solid fa-circle-play text-brand-green" /> Video fra arbejdet
                </h3>
                <div className="space-y-6">
                  {caseData.video.map((videoUrl, vIdx) => (
                    <div key={vIdx} className="rounded-2xl overflow-hidden bg-black border border-zinc-200 shadow-md">
                      <video
                        controls
                        playsInline
                        preload="metadata"
                        className="w-full max-h-[650px] object-contain mx-auto"
                      >
                        <source src={videoUrl} type="video/mp4" />
                        <source src={videoUrl} type="video/quicktime" />
                        Din browser understøtter ikke videoafspilning.
                      </video>
                      <div className="p-3 bg-zinc-900 text-right">
                        <a
                          href={videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-zinc-400 hover:text-brand-green transition-colors inline-flex items-center gap-1.5"
                        >
                          <i className="fa-solid fa-arrow-up-right-from-square" /> Åbn video i nyt vindue
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Gallery Section at the bottom */}
            {caseData.image && caseData.image.length > 0 && (
              <div className="pt-10 border-t border-zinc-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-zinc-900 flex items-center gap-2">
                    <i className="fa-regular fa-images text-brand-green" /> Galleri fra opgaven
                  </h3>
                  <span className="text-xs font-semibold text-zinc-500 bg-zinc-100 px-3 py-1 rounded-full">
                    {caseData.image.length} billeder
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {caseData.image.map((imgUrl, index) => (
                    <div 
                      key={index} 
                      className="group relative aspect-video rounded-xl overflow-hidden border border-zinc-200 shadow-sm hover:shadow-md transition-all cursor-pointer bg-zinc-100"
                      onClick={() => setActiveImageIndex(index)}
                    >
                      <img
                        src={getCdnUrl(imgUrl, 800)}
                        alt={`${caseData.title?.da} - Billede ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="bg-white/90 text-zinc-900 text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-sm">
                          <i className="fa-solid fa-magnifying-glass-plus text-brand-green" /> Se i fuld størrelse
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Back to Cases Link */}
            <div className="pt-8 border-t border-zinc-200 text-left">
              <Link
                to="/cases"
                className="inline-flex items-center gap-2 text-brand-green text-sm font-bold uppercase tracking-wider hover:text-brand-green-dark transition-colors"
              >
                <i className="fa-solid fa-arrow-left" /> Tilbage til alle cases
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImageIndex !== null && caseData.image && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setActiveImageIndex(null)}
        >
          <button
            onClick={() => setActiveImageIndex(null)}
            className="absolute top-6 right-6 text-white hover:text-brand-green text-3xl z-50 transition-colors cursor-pointer w-10 h-10 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60"
            aria-label="Luk billede"
          >
            <i className="fa-solid fa-xmark" />
          </button>

          {caseData.image.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveImageIndex((prev) => (prev === 0 ? caseData.image.length - 1 : prev - 1));
                }}
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white hover:text-brand-green text-2xl z-50 transition-colors cursor-pointer w-12 h-12 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/80"
                aria-label="Forrige billede"
              >
                <i className="fa-solid fa-chevron-left" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveImageIndex((prev) => (prev === caseData.image.length - 1 ? 0 : prev + 1));
                }}
                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white hover:text-brand-green text-2xl z-50 transition-colors cursor-pointer w-12 h-12 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/80"
                aria-label="Næste billede"
              >
                <i className="fa-solid fa-chevron-right" />
              </button>
            </>
          )}

          <div
            className="relative max-w-5xl max-h-[85vh] w-full flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={caseData.image[activeImageIndex]}
              alt={`${caseData.title?.da} - Billede ${activeImageIndex + 1}`}
              className="max-h-[80vh] w-auto max-w-full object-contain rounded-lg shadow-2xl"
            />
            <div className="mt-4 text-center text-white/80 text-xs font-mono">
              Billede {activeImageIndex + 1} af {caseData.image.length}
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="bg-zinc-900 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center flex flex-col items-center gap-5">
          <h2 className="text-2xl md:text-4xl font-bold text-white leading-tight">
            Har du en lignende opgave?
          </h2>
          <p className="text-zinc-300 text-sm md:text-base max-w-xl leading-relaxed">
            Vi dækker hele Danmark og kommer gerne ud og laver en prøverensning på din overflade.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-4 w-full sm:w-auto">
            <a
              href={siteSettings.phoneTel}
              className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-light text-zinc-950 font-bold px-8 py-3.5 rounded-lg text-xs uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-phone" /> Ring direkte: {siteSettings.phone}
            </a>
            <Link
              to="/kontakt"
              className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white font-bold px-8 py-3.5 rounded-lg text-xs uppercase tracking-wider border border-white/20 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-envelope" /> Udfyld formular
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
